import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  TextField,
  Modal,
  Box,
  Radio,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  X,
  Info,
  Plus,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { toPattern } from "vanilla-masker";
import { cnpj, cpf } from "cpf-cnpj-validator";
import { AnimatePresence, motion } from "framer-motion";
import {
  ModalHeader,
  ModalTitleContainer,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  StepperContainer,
  StepperWrapper,
  StepNumber,
  StepLabel,
  StepItem,
  StepDivider,
  ProgressBar,
  ProgressFill,
  TabsContainer,
  TabsList,
  TabButton,
  SearchContainer,
  SectionHeading,
  StickyHeader,
  StepContent,
  CloseButton,
  SourcesStepContainer,
  SearchIconWrapper,
  ModalWrapper,
  RiskContainer,
} from "./styles";
import { useExternalFont } from "contexts/externalFontContext";
import { RiskLevel, SearchType } from "types/enums";
import type {
  IAvailableExternalFontViewModel,
  IRiskMatrixViewModel,
} from "viewModels";
import { MASK_CPF, MASK_CNPJ } from "../../../constants";
import FontList from "components/FontList";
import { IRiskInputModel, IRiskMatrixInputModel } from "inputModels";
import RiskNameEditor from "../RiskNameEdit";
import { getByCnpj } from "services/consultCnpj";

interface DossierRequestModalProps {
  open: boolean;
  onClose: () => void;
  currentMatrix?: IRiskMatrixViewModel;
  onSubmit: (data: IRiskMatrixInputModel) => void;
}

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
  exit: { opacity: 0, y: 20, transition: { duration: 0.2 } },
};

const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 20 : -20,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.2 },
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 20 : -20,
    opacity: 0,
    transition: { duration: 0.2 },
  }),
};

const getRiskLevelColor = (level: RiskLevel) => {
  switch (level) {
    case RiskLevel.HIGH:
      return "#f44336";
    case RiskLevel.MEDIUM:
      return "#ff9800";
    case RiskLevel.LOW:
      return "#4caf50";
    default:
      return "#757575";
  }
};

const NewRiskMatrixModal = ({
  open,
  onClose,
  currentMatrix,
  onSubmit,
}: DossierRequestModalProps) => {
  const { externalFonts } = useExternalFont();
  const isEditing = !!currentMatrix;

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(0);
  const [documentType, setDocumentType] = useState<"cpf" | "cnpj">("cpf");
  const [searchTerm, setSearchTerm] = useState("");
  const [isValidDocument, setIsValidDocument] = useState(false);
  const [isValidName, setIsValidName] = useState(false);
  const [isNameLoading, setIsNameLoading] = useState(false);
  const [availableFonts, setAvailableFonts] = useState<
    IAvailableExternalFontViewModel[]
  >([]);
  const [document, setDocument] = useState(currentMatrix?.document || "");
  const [name, setName] = useState(currentMatrix?.name || "");
  const [risks, setRisks] = useState<IRiskInputModel[]>(
    currentMatrix?.risks || [
      {
        name: "Risco 1",
        level: RiskLevel.MEDIUM,
        externalFontsKeys: [],
      },
    ]
  );
  const [activeRiskTab, setActiveRiskTab] = useState<number>(0);

  const fontsForDocumentType = useMemo(() => {
    const typeToFilter =
      document.length === 14 ? SearchType.CNPJ : SearchType.CPF;
    return externalFonts.filter(
      (font) => font.type === typeToFilter || font.type === SearchType.BOTH
    );
  }, [document, externalFonts]);

  useEffect(() => {
    setAvailableFonts(fontsForDocumentType);
    setDocumentType(document.length === 14 ? "cnpj" : "cpf");
  }, [document, fontsForDocumentType]);

  useEffect(() => {
    const cleanDocument = document.replace(/\D/g, "");
    const expectedLength = documentType === "cnpj" ? 14 : 11;

    if (!cleanDocument) {
      setIsValidDocument(false);
    } else if (cleanDocument.length === expectedLength) {
      const isValid =
        documentType === "cnpj"
          ? cnpj.isValid(cleanDocument)
          : cpf.isValid(cleanDocument);
      setIsValidDocument(isValid);
    } else {
      setIsValidDocument(false);
    }
  }, [document, documentType]);

  useEffect(() => {
    const fetchCompanyName = async () => {
      if (
        documentType === "cnpj" &&
        isValidDocument &&
        document.length === 14
      ) {
        setIsNameLoading(true);
        try {
          const response = await getByCnpj(document);
          setName(response?.companyName || "");
        } catch (error) {
          setName("");
        } finally {
          setIsNameLoading(false);
        }
      }
    };
    if (!isEditing) fetchCompanyName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, documentType, isValidDocument]);

  useEffect(() => {
    setIsValidName(name.trim().length > 0);
  }, [name]);

  const filteredFonts = useMemo(() => {
    return availableFonts
      .filter((font) =>
        !font.disabled && font.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [availableFonts, searchTerm]);

  const handleSourceToggle = useCallback(
    (sourceKey: string) => {
      setRisks((prevRisks) =>
        prevRisks.map((risk, index) =>
          index === activeRiskTab
            ? {
                ...risk,
                externalFontsKeys: risk.externalFontsKeys.includes(sourceKey)
                  ? risk.externalFontsKeys.filter((key) => key !== sourceKey)
                  : [...risk.externalFontsKeys, sourceKey],
              }
            : risk
        )
      );
    },
    [activeRiskTab]
  );

  const addNewRisk = useCallback(() => {
    const newRisk: IRiskInputModel = {
      name: `Risco ${risks.length + 1}`,
      level: RiskLevel.MEDIUM,
      externalFontsKeys: [],
    };
    setRisks((prev) => [...prev, newRisk]);
    setActiveRiskTab(risks.length);
  }, [risks.length]);

  const removeRisk = useCallback(
    (riskIndex: number) => {
      if (risks.length <= 1) return;
      setRisks((prev) => prev.filter((_, index) => index !== riskIndex));
      if (activeRiskTab === riskIndex) {
        const remainingRisks = risks.filter((_, index) => index !== riskIndex);
        setActiveRiskTab(remainingRisks.length > 0 ? 0 : 0);
      }
    },
    [risks, activeRiskTab]
  );

  const handleRiskLevelChange = useCallback(
    (riskIndex: number, level: RiskLevel) => {
      setRisks((prev) =>
        prev.map((risk, index) =>
          index === riskIndex ? { ...risk, level } : risk
        )
      );
    },
    []
  );

  const validateRisks = useCallback(() => {
    return (
      risks.length > 0 &&
      risks.every(
        (risk) => risk.name.trim() && risk.externalFontsKeys.length > 0
      )
    );
  }, [risks]);

  const getRisksWithoutSources = useCallback(() => {
    return risks
      .filter((risk) => risk.externalFontsKeys.length === 0)
      .map((risk) => risk.name)
      .join(", ");
  }, [risks]);

  const nextStep = useCallback(async () => {
    if (step === 1 && (!document || !isValidDocument || !isValidName)) return;
    if (step < 2) {
      setDirection(1);
      setStep(step + 1);
    }
  }, [step, document, isValidDocument, isValidName]);

  const prevStep = useCallback(() => {
    if (step > 1) {
      setDirection(-1);
      setStep(step - 1);
    }
  }, [step]);

  const handleSubmit = useCallback(() => {
    if (!isValidDocument || !document || !isValidName || !validateRisks())
      return;

    const cleanedDocument = document.replace(/\D/g, "");

    onSubmit({
      document: cleanedDocument,
      name,
      risks,
    });

    if (!isEditing) {
      setDocument("");
      setName("");
      setRisks([
        {
          name: "Risco 1",
          level: RiskLevel.MEDIUM,
          externalFontsKeys: [],
        },
      ]);
      setActiveRiskTab(0);
      setStep(1);
      setSearchTerm("");
    }

    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isValidDocument,
    document,
    isValidName,
    risks,
    isEditing,
    onSubmit,
    onClose,
    validateRisks,
  ]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <ModalWrapper>
        <ModalHeader>
          <ModalTitleContainer>
            <ModalTitle id="modal-title">
              {isEditing ? "Editar Matriz de Risco" : "Nova Matriz de Risco"}
            </ModalTitle>
            <ModalDescription id="modal-description">
              {isEditing
                ? "Edite as informações da matriz de riscos"
                : "Complete as etapas para gerar uma nova matriz de riscos"}
            </ModalDescription>
          </ModalTitleContainer>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <StepperContainer>
            <StepperWrapper>
              <StepItem>
                <StepNumber active={step >= 1}>1</StepNumber>
                <StepLabel>Documento</StepLabel>
              </StepItem>
              <StepDivider />
              <StepItem>
                <StepNumber active={step >= 2}>2</StepNumber>
                <StepLabel>Riscos</StepLabel>
              </StepItem>
            </StepperWrapper>
            <ProgressBar>
              <ProgressFill progress={(step / 2) * 100} />
            </ProgressBar>
          </StepperContainer>
          <AnimatePresence>
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={containerVariants}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                }}
              >
                {step === 1 && (
                  <StepContent
                    key="step1"
                    custom={direction}
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                  >
                    <SectionHeading>
                      {isEditing
                        ? "Revise o documento da matriz"
                        : "Informe o documento para a matriz"}
                    </SectionHeading>
                    <TabsContainer>
                      <TabsList>
                        <TabButton
                          active={documentType === "cpf"}
                          onClick={() => setDocumentType("cpf")}
                          disabled={isEditing}
                        >
                          CPF
                        </TabButton>
                        <TabButton
                          active={documentType === "cnpj"}
                          onClick={() => setDocumentType("cnpj")}
                          disabled={isEditing}
                        >
                          CNPJ
                        </TabButton>
                      </TabsList>
                    </TabsContainer>
                    <TextField
                      name="document"
                      fullWidth
                      size="small"
                      label={
                        documentType === "cpf"
                          ? "Informe o CPF"
                          : "Informe o CNPJ"
                      }
                      slotProps={{
                        htmlInput: {
                          maxLength: 18,
                        },
                      }}
                      placeholder={
                        documentType === "cpf"
                          ? "000.000.000-00"
                          : "00.000.000/0000-00"
                      }
                      value={toPattern(
                        document,
                        documentType === "cpf" ? MASK_CPF : MASK_CNPJ
                      )}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/\D/g, "");
                        setDocument(rawValue);
                      }}
                      error={
                        document
                          ? document.length ===
                              (documentType === "cnpj" ? 14 : 11) &&
                            !isValidDocument
                          : false
                      }
                      helperText={
                        document
                          ? document.length <
                            (documentType === "cnpj" ? 14 : 11)
                            ? "Documento incompleto"
                            : !isValidDocument &&
                              document.length ===
                                (documentType === "cnpj" ? 14 : 11)
                            ? "Documento inválido"
                            : undefined
                          : undefined
                      }
                      sx={{ marginBottom: 2 }}
                      disabled={isEditing}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label={documentType === "cpf" ? "Nome" : "Razão"}
                      placeholder="Adicione o nome da empresa ou pessoa"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      error={!isValidName && name.length > 0}
                      helperText={
                        !isValidName && name.length > 0
                          ? "Nome é obrigatório"
                          : undefined
                      }
                      sx={{ marginBottom: 2 }}
                      disabled={isNameLoading || isEditing}
                      InputProps={{
                        endAdornment: isNameLoading ? (
                          <CircularProgress size={20} />
                        ) : null,
                      }}
                    />
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: "grey.100",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "grey.300",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <Info
                        size={28}
                        color="#293778"
                        style={{ marginTop: "2px", flexShrink: 0 }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 2 }}
                      >
                        <strong>Dica:</strong>{" "}
                        {isEditing
                          ? "Revise ou edite o nome da pessoa ou empresa."
                          : "Preencha o CPF ou CNPJ e o nome da pessoa ou empresa."}
                      </Typography>
                    </Box>
                  </StepContent>
                )}

                {step === 2 && (
                  <StepContent
                    key="step2"
                    custom={direction}
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                  >
                    <SourcesStepContainer>
                      <StickyHeader>
                        <SectionHeading>
                          {isEditing
                            ? "Edite os Riscos da Matriz"
                            : "Gerencie os Riscos da Matriz"}
                        </SectionHeading>
                        <Box
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            mb: 3,
                          }}
                        >
                          <TabsContainer>
                            <TabsList>
                              {risks.map((risk, index) => (
                                <TabButton
                                  key={index}
                                  active={activeRiskTab === index}
                                  onClick={() => setActiveRiskTab(index)}
                                  style={{ position: "relative" }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: getRiskLevelColor(
                                          risk.level
                                        ),
                                        borderRadius: 1,
                                        flexShrink: 0,
                                      }}
                                    />
                                    <RiskNameEditor
                                      risk={risk}
                                      riskIndex={index}
                                      onNameChange={(idx, name) =>
                                        setRisks((prev) =>
                                          prev.map((r, i) =>
                                            i === idx ? { ...r, name } : r
                                          )
                                        )
                                      }
                                      onRemove={removeRisk}
                                    />
                                  </Box>
                                </TabButton>
                              ))}
                              <TabButton active={false} onClick={addNewRisk}>
                                <Plus size={16} />
                              </TabButton>
                            </TabsList>
                          </TabsContainer>
                        </Box>
                        {activeRiskTab < risks.length && (
                          <TabsContainer>
                            <RiskContainer>
                              <Typography variant="body2">
                                Nível de Risco
                              </Typography>
                              <div className="radio-group">
                                <div className="label">
                                  <Radio
                                    disableRipple
                                    checked={
                                      risks[activeRiskTab]?.level ===
                                      RiskLevel.HIGH
                                    }
                                    onChange={() =>
                                      handleRiskLevelChange(
                                        activeRiskTab,
                                        RiskLevel.HIGH
                                      )
                                    }
                                  />
                                  <AlertCircle className="icon-high" />
                                  <p>Alto</p>
                                </div>
                                <div className="label">
                                  <Radio
                                    disableRipple
                                    checked={
                                      risks[activeRiskTab]?.level ===
                                      RiskLevel.MEDIUM
                                    }
                                    onChange={() =>
                                      handleRiskLevelChange(
                                        activeRiskTab,
                                        RiskLevel.MEDIUM
                                      )
                                    }
                                  />
                                  <AlertTriangle className="icon-medium" />
                                  <p>Médio</p>
                                </div>
                                <div className="label">
                                  <Radio
                                    disableRipple
                                    checked={
                                      risks[activeRiskTab]?.level ===
                                      RiskLevel.LOW
                                    }
                                    onChange={() =>
                                      handleRiskLevelChange(
                                        activeRiskTab,
                                        RiskLevel.LOW
                                      )
                                    }
                                  />
                                  <CheckCircle2 className="icon-low" />
                                  <p>Baixo</p>
                                </div>
                              </div>
                            </RiskContainer>
                            <SearchContainer>
                              <SearchIconWrapper>
                                <Search size={16} />
                              </SearchIconWrapper>
                              <TextField
                                size="small"
                                placeholder="Pesquisar Fontes"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{
                                  width: "350px",
                                  "& .MuiOutlinedInput-root": {
                                    paddingLeft: "32px",
                                  },
                                }}
                              />
                            </SearchContainer>
                          </TabsContainer>
                        )}
                        {activeRiskTab < risks.length && (
                          <Box sx={{ flex: 1, minHeight: "300px" }}>
                            <FontList
                              fonts={filteredFonts}
                              selectedFonts={
                                risks[activeRiskTab]?.externalFontsKeys || []
                              }
                              onFontToggle={handleSourceToggle}
                              searchTerm={searchTerm}
                            />
                          </Box>
                        )}
                      </StickyHeader>
                    </SourcesStepContainer>
                  </StepContent>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </ModalContent>
        <ModalFooter>
          {step > 1 ? (
            <Button
              variant="outlined"
              onClick={prevStep}
              sx={{ color: "#293778", borderColor: "#293778" }}
            >
              Voltar
            </Button>
          ) : (
            <div></div>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Tooltip
              title={
                step === 2 && !validateRisks()
                  ? `Riscos sem fontes: ${getRisksWithoutSources()}`
                  : ""
              }
              disableHoverListener={step !== 2 || validateRisks()}
            >
              <span>
                <Button
                  variant="contained"
                  onClick={step < 2 ? nextStep : handleSubmit}
                  disabled={
                    step === 1
                      ? !isValidDocument || !isValidName
                      : !validateRisks()
                  }
                  sx={{
                    backgroundColor: "#293778",
                    "&:hover": { backgroundColor: "#1e2654" },
                  }}
                >
                  {step < 2
                    ? "Próximo"
                    : isEditing
                    ? "Salvar matriz"
                    : "Criar matriz"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </ModalFooter>
      </ModalWrapper>
    </Modal>
  );
};

export default NewRiskMatrixModal;
